import { useRef, useState, type ChangeEvent, type DragEvent, type FormEvent } from 'react';
import { COUNTRIES } from '@/config/countryCodes';

const FORM_URL = '/api/invoice-design-request';
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const ALLOWED_ATTACHMENT_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.webp'];
const ALLOWED_ATTACHMENT_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
];

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function getFileExtension(fileName: string): string {
  const dotIndex = fileName.lastIndexOf('.');
  return dotIndex >= 0 ? fileName.slice(dotIndex).toLowerCase() : '';
}

export default function InvoiceDesignForm() {
  const [selectedCountry, setSelectedCountry] = useState('United States');
  const [description, setDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileSize, setFileSize] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);

  const selectedDial = COUNTRIES.find((c) => c.name === selectedCountry)?.dial || '+1';

  const clearFile = () => {
    setSelectedFile(null);
    setFileName(null);
    setFileSize(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const resetFormState = (form: HTMLFormElement) => {
    form.reset();
    setDescription('');
    clearFile();
    setSelectedCountry('United States');
  };

  const handleFile = (file: File) => {
    setFormStatus('idle');
    setFormError('');

    const extension = getFileExtension(file.name);
    const mimeType = file.type.toLowerCase();
    const extensionAllowed = ALLOWED_ATTACHMENT_EXTENSIONS.includes(extension);
    const mimeAllowed =
      mimeType === '' ||
      mimeType === 'application/octet-stream' ||
      ALLOWED_ATTACHMENT_MIME_TYPES.includes(mimeType);

    if (!extensionAllowed || !mimeAllowed) {
      clearFile();
      setFormStatus('error');
      setFormError('Please attach a PDF, JPG, PNG, or WebP file.');
      return;
    }

    if (file.size <= 0) {
      clearFile();
      setFormStatus('error');
      setFormError('The selected attachment is empty. Please choose another file.');
      return;
    }

    if (file.size > MAX_ATTACHMENT_BYTES) {
      clearFile();
      setFormStatus('error');
      setFormError('The attachment is too large. The maximum file size is 10 MB.');
      return;
    }

    setSelectedFile(file);
    setFileName(file.name);
    setFileSize(formatBytes(file.size));
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = ((formData.get('website_alt') as string) || '').trim();
    if (honeypot) {
      setFormStatus('success');
      resetFormState(form);
      return;
    }

    formData.delete('attachment');
    if (selectedFile) {
      formData.append('attachment', selectedFile, selectedFile.name);
    }

    setIsSubmitting(true);
    setFormStatus('idle');
    setFormError('');

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        body: formData,
      });

      const responseText = await response.text();
      let parsed:
        | {
            code?: string;
            message?: string;
            requestId?: string;
            meta?: { message?: string; detail?: string };
          }
        | null = null;

      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const message = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '';

      if (response.ok && code === 'OK') {
        setFormStatus('success');
        resetFormState(form);
      } else {
        setFormStatus('error');
        setFormError(message || 'Something went wrong. Please try again.');
      }
    } catch {
      setFormStatus('error');
      setFormError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      id="custom-invoice-design-form"
      encType="multipart/form-data"
      className="space-y-6"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-2">
            First Name *
          </label>
          <input
            type="text"
            id="first_name"
            name="first_name"
            required
            maxLength={100}
            autoComplete="given-name"
            placeholder="Jane"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
        </div>
        <div>
          <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-2">
            Last Name *
          </label>
          <input
            type="text"
            id="last_name"
            name="last_name"
            required
            maxLength={100}
            autoComplete="family-name"
            placeholder="Doe"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
        </div>
      </div>

      <div>
        <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
          Company Name *
        </label>
        <input
          type="text"
          id="company"
          name="company"
          required
          maxLength={200}
          autoComplete="organization"
          placeholder="Your company"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
        />
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
          Email Address *
        </label>
        <input
          type="email"
          id="email"
          name="email"
          required
          maxLength={254}
          autoComplete="email"
          placeholder="you@company.com"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
        />
      </div>

      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
          Phone Number *
        </label>
        <div className="flex gap-2">
          <select
            id="country"
            name="country"
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            className="w-28 px-2 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white flex-shrink-0"
            aria-label="Country code"
          >
            {COUNTRIES.map((country) => (
              <option key={country.name} value={country.name} title={country.name}>
                {country.flag} {country.dial}
              </option>
            ))}
          </select>
          <input type="hidden" name="dial_code" value={selectedDial} />
          <input
            type="tel"
            id="phone"
            name="phone"
            required
            maxLength={60}
            autoComplete="tel"
            placeholder="(555) 123-4567"
            className="flex-1 min-w-0 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
        </div>
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
          Tell us about your design
        </label>
        <textarea
          id="description"
          name="description"
          value={description}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
          rows={6}
          maxLength={500}
          placeholder={
            'Describe the invoice layout you have in mind — colors, logo placement, sections, fields, or anything else you want us to know.\n\nYou can add multiple lines.'
          }
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-y"
        ></textarea>
        <p className="text-xs text-gray-500 mt-1 text-right">{description.length}/500 characters</p>
      </div>

      <div>
        <span className="block text-sm font-medium text-gray-700 mb-2">Example of your design</span>
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
            isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'
          }`}
        >
          <input
            type="file"
            ref={fileInputRef}
            name="attachment"
            accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const file = e.target.files?.[0];
              if (file) {
                handleFile(file);
              }
            }}
          />
          <i className="ri-upload-cloud-2-line text-3xl text-gray-400 w-8 h-8 flex items-center justify-center mx-auto mb-3"></i>
          <p className="text-sm text-gray-600 mb-1">
            If you have an example of your design, please attach it here
          </p>
          <p className="text-xs text-gray-400 mb-4">Drag &amp; drop a file here, or</p>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 font-semibold px-5 py-2 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer text-sm"
          >
            <i className="ri-attachment-2 w-4 h-4 flex items-center justify-center"></i>
            Attach
          </button>

          {fileName && (
            <div className="mt-4 flex items-center justify-between gap-3 bg-white border border-gray-200 rounded-lg px-4 py-3 text-left">
              <div className="flex items-center gap-3 min-w-0">
                <i className="ri-file-line text-gray-400 text-xl w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                <div className="min-w-0">
                  <p className="text-sm text-gray-700 font-medium truncate">{fileName}</p>
                  <p className="text-xs text-gray-400">{fileSize}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={clearFile}
                className="text-gray-400 hover:text-red-500 transition-colors cursor-pointer flex-shrink-0"
                aria-label="Remove file"
              >
                <i className="ri-close-circle-line text-xl w-5 h-5 flex items-center justify-center"></i>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="hp-guard" aria-hidden="true">
        <label htmlFor="website_alt">Website</label>
        <input
          type="text"
          id="website_alt"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-blue-600 text-white py-3.5 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer text-base"
      >
        {isSubmitting ? (
          <span className="inline-flex items-center justify-center gap-2">
            <i className="ri-loader-4-line animate-spin w-5 h-5 flex items-center justify-center"></i>
            Sending...
          </span>
        ) : (
          <span className="inline-flex items-center justify-center gap-2">
            <i className="ri-send-plane-line w-5 h-5 flex items-center justify-center"></i>
            Send Request
          </span>
        )}
      </button>

      {formStatus === 'success' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <i className="ri-checkbox-circle-line text-green-500 text-xl mr-3 w-5 h-5 flex items-center justify-center"></i>
            <p className="text-green-700 text-sm">
              Thank you! Your request has been received. Our team will be in touch shortly.
            </p>
          </div>
        </div>
      )}

      {formStatus === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <i className="ri-error-warning-line text-red-500 text-xl mr-3 w-5 h-5 flex items-center justify-center"></i>
            <p className="text-red-700 text-sm">
              {formError || 'Sorry, something went wrong. Please try again.'}
            </p>
          </div>
        </div>
      )}
    </form>
  );
}
